import { API_BASE } from '../Constants';

// Tipos das Tarefas Agendadas. Espelham os campos serializados pelo router
// api/app/routers/tasks.py — datas chegam como string ISO (ou null).

export type ScheduleType = 'once' | 'daily' | 'weekly' | 'monthly';

export interface TaskSchedule {
  type: ScheduleType;
  time: string; // "HH:MM"
  weekday?: number; // 0=segunda .. 6=domingo (só weekly)
  day_of_month?: number; // 1..28 (só monthly)
  date?: string; // "YYYY-MM-DD" (só once)
}

// "in_app" | "email" | "slack:<alias>". O "slack" pelado é legado (bindings
// antigos, antes dos múltiplos webhooks) e a UI o preserva quando intocado.
export type DeliveryChannel = 'in_app' | 'email' | 'slack' | `slack:${string}`;

export type TaskStatus =
  | 'active'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed_paused';

export interface ScheduledTask {
  task_id: string;
  title: string;
  prompt: string;
  chat_id: string;
  schedule: TaskSchedule;
  timezone: string;
  delivery: DeliveryChannel[];
  status: TaskStatus;
  next_run_at: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  unread: boolean;
  consecutive_failures: number;
  // Modelo da conversa onde a tarefa nasceu (null = padrão do servidor).
  model?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface TaskRunDelivery {
  channel: string;
  ok: boolean;
  error?: string;
}

export interface TaskRun {
  started_at: string;
  status: string;
  notified: boolean;
  message_preview?: string;
  error?: string;
  duration_seconds?: number;
  deliveries?: TaskRunDelivery[];
}

export interface ListTasksResponse {
  tasks: ScheduledTask[];
  max_active_tasks: number;
  is_premium?: boolean;
}

export interface CreateTaskPayload {
  title: string;
  prompt: string;
  schedule: TaskSchedule;
  delivery: DeliveryChannel[];
  timezone?: string;
}

export interface UpdateTaskPayload {
  status?: 'active' | 'paused';
  title?: string;
  prompt?: string;
  schedule?: TaskSchedule;
  delivery?: DeliveryChannel[];
}

// Erro que carrega o status HTTP, pra UI tratar 402 (premium) e 409 (limite).
export class TaskApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'TaskApiError';
    this.status = status;
  }
}

async function parseError(res: Response): Promise<TaskApiError> {
  let message = `Erro ${res.status}`;
  try {
    const data = await res.json();
    if (data && typeof data.message === 'string') message = data.message;
  } catch {
    /* corpo não-JSON: mantém a mensagem genérica */
  }
  return new TaskApiError(res.status, message);
}

function authHeaders(token: string, json = false): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Auth ${token}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export async function listTasks(token: string): Promise<ListTasksResponse> {
  const res = await fetch(`${API_BASE}/api/chat/tasks`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function createTask(
  token: string,
  payload: CreateTaskPayload,
): Promise<ScheduledTask> {
  const res = await fetch(`${API_BASE}/api/chat/tasks`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function updateTask(
  token: string,
  taskId: string,
  payload: UpdateTaskPayload,
): Promise<ScheduledTask> {
  const res = await fetch(`${API_BASE}/api/chat/tasks/${taskId}`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function deleteTask(token: string, taskId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/chat/tasks/${taskId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) throw await parseError(res);
}

export async function runTaskNow(token: string, taskId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/chat/tasks/${taskId}/run`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) throw await parseError(res);
}

export async function listTaskRuns(
  token: string,
  taskId: string,
): Promise<TaskRun[]> {
  const res = await fetch(`${API_BASE}/api/chat/tasks/${taskId}/runs`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw await parseError(res);
  const data = await res.json();
  return data.runs || [];
}

// ===== Canais de notificação (Slack etc.) =====
// A API nunca devolve o endereço completo, só o mascarado + o estado.

export type NotificationChannelName = 'slack';

// O Slack aceita vários bindings por usuário, cada um com um `alias` amigável
// ("familia", "time-eng") e verificação própria. A tarefa referencia o destino
// como "slack:<alias>" na lista `delivery`.
export interface NotificationChannel {
  channel: NotificationChannelName;
  alias: string;
  address_masked: string;
  verified: boolean;
}

export async function listNotificationChannels(
  token: string,
): Promise<NotificationChannel[]> {
  const res = await fetch(`${API_BASE}/api/chat/notification-channels`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw await parseError(res);
  const data = await res.json();
  return data.channels || [];
}

/** Vincula um canal (envia o código de verificação pro endereço informado). */
export async function connectNotificationChannel(
  token: string,
  channel: NotificationChannelName,
  address: string,
  alias: string,
): Promise<NotificationChannel> {
  const res = await fetch(`${API_BASE}/api/chat/notification-channels`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ channel, address, alias }),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function verifyNotificationChannel(
  token: string,
  channel: NotificationChannelName,
  code: string,
  alias: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/chat/notification-channels/${channel}/verify`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ code, alias }),
    },
  );
  if (!res.ok) throw await parseError(res);
}

export async function disconnectNotificationChannel(
  token: string,
  channel: NotificationChannelName,
  alias: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/chat/notification-channels/${channel}?alias=${encodeURIComponent(alias)}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );
  if (!res.ok) throw await parseError(res);
}
